import { XClose } from "@untitledui/icons";
import type { ReactNode } from "react";
import { Toggle } from "@/components/base/toggle/toggle";
import { cx } from "@/utils/cx";
import {
  type EditDisabledReason,
  LockableToggle,
  SKIPPED_STEP_NOTE,
  type Step,
  StepGlyph,
  stepLabel,
} from "./sequence-steps-preview";

const LARK_STEP_BLOCKED_NOTE =
  "Needs to be configured and authorised with your Merchant Lark account to work. Please contact a member of our team if you would like to set this up.";

type NotifyTiktokStepCardProps = {
  step: Step;
  badge: ReactNode;
  title?: string;
  subtitle?: string;
  canToggle: boolean;
  disabledReason?: EditDisabledReason;
  larkBlocked?: boolean;
  onToggle: (enabled: boolean) => void;
  className?: string;
};

const ErrorMarker = () => (
  <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full shrink-0 mt-2.5 bg-error-50 border border-error-100 text-error-500">
    <XClose className="w-[19px] h-[19px]" />
  </div>
);

export const NotifyTiktokStepCard = ({
  step,
  badge,
  title,
  subtitle,
  canToggle,
  disabledReason,
  larkBlocked = false,
  onToggle,
  className,
}: NotifyTiktokStepCardProps) => {
  const label = title ?? stepLabel(step);
  const isEnabled = step.enabled !== false;

  const toggle = (canToggle || disabledReason) && (
    <LockableToggle reason={canToggle ? undefined : disabledReason}>
      <Toggle
        isSelected={isEnabled}
        isDisabled={!canToggle}
        onChange={onToggle}
        aria-label={`${stepLabel(step)} ${isEnabled ? "enabled" : "disabled"}`}
      />
    </LockableToggle>
  );

  if (larkBlocked) {
    return (
      <div className={cx("flex items-start gap-3", className)}>
        <ErrorMarker />
        <div className="flex flex-col flex-1 rounded-lg border overflow-hidden border-error border-dashed bg-error-25">
          <div className="flex items-center gap-2.5 px-4 py-3">
            <StepGlyph type={step.type} className="w-5 h-5 text-tertiary" />
            <span className="text-sm font-medium text-secondary flex-1">{label}</span>
          </div>
          <div className="px-4 pb-3 text-sm text-tertiary">Sends a demonetisation request to TikTok on Lark</div>
          <div className="border-t border-error-200 bg-error-25 px-4 py-4 flex flex-col items-start gap-1">
            <p className="text-xs font-medium text-quaternary">Not available</p>
            <p className="text-sm text-tertiary font-medium">{LARK_STEP_BLOCKED_NOTE}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className={cx("flex items-start gap-3", className)}>
        <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium shrink-0 mt-2.5 bg-secondary-solid text-white">
          {badge}
        </div>
        <div className="flex flex-col flex-1 rounded-lg border overflow-hidden border-primary border-dashed bg-primary">
          <div className="flex items-center gap-2.5 px-4 py-3">
            <StepGlyph type={step.type} className="w-5 h-5 text-fg-error-primary" />
            <span className="text-sm font-medium text-secondary flex-1">{label}</span>
            {toggle}
          </div>
          <div className="px-4 pb-3 text-sm text-error-primary">{SKIPPED_STEP_NOTE}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("flex items-start gap-3", className)}>
      <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium shrink-0 mt-2.5 bg-secondary-solid text-white">
        {badge}
      </div>
      <div className="flex flex-col flex-1 rounded-lg border overflow-hidden border-secondary bg-unbox-cream-50">
        <div className="flex items-center gap-2.5 px-4 py-3">
          <StepGlyph type={step.type} className="w-5 h-5 text-tertiary" />
          <span className="text-sm font-medium text-secondary flex-1">{label}</span>
          {toggle}
        </div>
        {subtitle && <div className="px-4 pb-3 text-sm text-tertiary">{subtitle}</div>}
      </div>
    </div>
  );
};
